import { useEffect, useState } from "react";
import { BookOpen, Plus, Trash2, Pencil, Loader2, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { DEMO_SOPS } from "./mdtDemo";
import { inputCls, btnCls, toastOk } from "./RosterTab";

/* SOP library — every unit reads standing operating procedures; command publishes/edits. */

export default function SopsTab({ G }) {
  const { serverId, token, demo, sfx, me, config } = G;
  const [sops, setSops] = useState(null);
  const [open, setOpen] = useState("");
  const [editing, setEditing] = useState(null); // null | {} new | sop
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (demo) { setSops(DEMO_SOPS); return; }
    api.get(`/nc/servers/${serverId}/cad/dept/sops`, { params: { token } })
      .then((r) => setSops(r.data.sops)).catch(() => setSops([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title?.trim() || (editing.body || "").trim().length < 10) return;
    sfx.keyTick();
    if (demo) {
      setSops((s) => editing.id ? s.map((x) => x.id === editing.id ? { ...x, ...editing } : x) : [{ ...editing, id: `l${Date.now()}`, author: me.name, updated_at: new Date().toISOString() }, ...s]);
      setEditing(null); return;
    }
    setBusy(true);
    try {
      const body = { title: editing.title.trim(), body: editing.body, dept_id: editing.dept_id || "" };
      const r = editing.id
        ? await api.put(`/nc/servers/${serverId}/cad/dept/sops/${editing.id}`, body, { params: { token } })
        : await api.post(`/nc/servers/${serverId}/cad/dept/sops`, body, { params: { token } });
      toastOk(r.data.message); setEditing(null); load();
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(false); }
  };

  if (!sops) return <div className="flex justify-center p-8"><Loader2 className="w-4 h-4 animate-spin text-sky-400" /></div>;

  return (
    <div className="p-3.5 flex flex-col gap-2" data-testid="sops-tab">
      {me.is_command && (
        <button data-testid="sop-new" onClick={() => { setEditing({ title: "", body: "", dept_id: "" }); sfx.keyTick(); }}
          className={`${btnCls} border-sky-500/50 text-sky-300 hover:bg-sky-500/10 w-fit flex items-center gap-1`}><Plus className="w-3 h-3" /> Publish SOP</button>
      )}
      {editing && (
        <div className="border border-sky-500/30 bg-sky-500/5 p-3 flex flex-col gap-2" data-testid="sop-editor">
          <div className="flex gap-1.5">
            <input data-testid="sop-title" value={editing.title} onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))} placeholder="SOP title" className={`${inputCls} flex-1`} />
            <select data-testid="sop-dept" value={editing.dept_id || ""} onChange={(e) => setEditing((s) => ({ ...s, dept_id: e.target.value }))} className={inputCls}>
              <option value="">All departments</option>
              {config.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <textarea data-testid="sop-body" value={editing.body} onChange={(e) => setEditing((s) => ({ ...s, body: e.target.value }))} rows={7} placeholder="Numbered procedure, one step per line…" className={`${inputCls} w-full resize-y`} />
          <div className="flex gap-2">
            <button data-testid="sop-save" onClick={save} disabled={busy} className={`${btnCls} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10`}>{busy ? "Saving…" : editing.id ? "Save changes" : "Publish"}</button>
            <button data-testid="sop-cancel" onClick={() => setEditing(null)} className={`${btnCls} border-white/15 text-zinc-400`}>Cancel</button>
          </div>
        </div>
      )}
      {sops.map((s) => {
        const dept = config.departments.find((d) => d.id === s.dept_id);
        return (
          <div key={s.id} data-testid={`sop-${s.id.slice(0, 6)}`} className="border border-white/10 bg-black/40">
            <button data-testid={`sop-open-${s.id.slice(0, 6)}`} onClick={() => { setOpen(open === s.id ? "" : s.id); sfx.keyTick(); }}
              className="w-full flex items-center gap-2.5 p-3 text-left">
              <BookOpen className="w-3.5 h-3.5 text-sky-400 shrink-0" />
              <span className="font-mono text-[10.5px] font-bold text-zinc-100 flex-1 min-w-0 truncate">{s.title}</span>
              <span className="font-mono text-[7.5px] uppercase text-zinc-500">{dept ? dept.abbr || dept.name : "All depts"} · {s.author}</span>
              <ChevronDown className={`w-3 h-3 text-zinc-500 transition-transform ${open === s.id ? "rotate-180" : ""}`} />
            </button>
            {open === s.id && (
              <div className="px-3 pb-3">
                <pre className="font-mono text-[9.5px] text-zinc-300 whitespace-pre-wrap leading-relaxed border-t border-white/10 pt-2">{s.body}</pre>
                {me.is_command && (
                  <div className="flex gap-1.5 mt-2">
                    <button data-testid={`sop-edit-${s.id.slice(0, 6)}`} onClick={() => { setEditing({ ...s }); sfx.keyTick(); }} className={`${btnCls} border-sky-500/40 text-sky-300 flex items-center gap-1`}><Pencil className="w-3 h-3" /> Edit</button>
                    <button data-testid={`sop-del-${s.id.slice(0, 6)}`} onClick={() => { sfx.keyTick(); if (demo) setSops((x) => x.filter((y) => y.id !== s.id)); else api.delete(`/nc/servers/${serverId}/cad/dept/sops/${s.id}`, { params: { token } }).then(load); }} className={`${btnCls} border-red-500/40 text-red-300 flex items-center gap-1`}><Trash2 className="w-3 h-3" /> Delete</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {sops.length === 0 && <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] p-3 text-center">No SOPs published yet</p>}
    </div>
  );
}
